import React, { useState, useEffect, useCallback } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Save, Loader2, Terminal } from "lucide-react";

export default function GroupCommandsPage() {
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchCommands = useCallback(async () => {
    try { const { data } = await authAxios.get(`${API}/group-commands`); setCommands(data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCommands(); }, [fetchCommands]);

  const startNew = () => setEditing({ command: ".", response_type: "text", response_content: "", media_url: "", is_active: true });
  const startEdit = (cmd) => setEditing({ ...cmd });

  const handleSave = async () => {
    if (!editing.command || !editing.command.startsWith(".")) return;
    setSaving(true);
    try {
      if (editing.id) { await authAxios.put(`${API}/group-commands/${editing.id}`, editing); }
      else { await authAxios.post(`${API}/group-commands`, editing); }
      setEditing(null); fetchCommands();
    } catch (err) { alert(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this command?")) return;
    try { await authAxios.delete(`${API}/group-commands/${id}`); fetchCommands(); }
    catch (err) { console.error(err); }
  };

  if (loading) return <div className="p-8"><div className="animate-pulse h-64 bg-slate-200 rounded-xl" /></div>;

  return (
    <div className="p-8" data-testid="group-commands-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#0f172a] flex items-center gap-2"><Terminal className="w-6 h-6" /> Group Commands</h1>
          <p className="text-sm text-muted-foreground mt-1">Define dot-commands like .menu .help .price for group chats</p>
        </div>
        <Button onClick={startNew} className="bg-[#0f172a] hover:bg-[#1e293b]" data-testid="create-command-btn"><Plus className="w-4 h-4 mr-2" /> New Command</Button>
      </div>

      {commands.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><Terminal className="w-10 h-10 mx-auto mb-3 opacity-30" /><p className="text-sm">No commands yet</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {commands.map((cmd) => (
            <Card key={cmd.id} data-testid={`cmd-${cmd.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-bold bg-slate-100 px-2 py-0.5 rounded">{cmd.command}</code>
                    <Badge className={cmd.is_active ? "bg-[#22c55e] text-white" : "bg-slate-200 text-slate-600"}>{cmd.is_active ? "Active" : "Inactive"}</Badge>
                    <Badge variant="outline" className="text-xs">{cmd.response_type}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate max-w-md">{cmd.response_content || cmd.media_url || "No content"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => startEdit(cmd)} data-testid={`edit-cmd-${cmd.id}`}>Edit</Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" onClick={() => handleDelete(cmd.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="command-editor">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Command" : "New Command"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Command (must start with .)</Label>
                <Input value={editing.command} onChange={(e) => setEditing({ ...editing, command: e.target.value })} placeholder=".menu" data-testid="cmd-input" />
              </div>
              <div className="space-y-2">
                <Label>Response Type</Label>
                <select value={editing.response_type} onChange={(e) => setEditing({ ...editing, response_type: e.target.value })} className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm" data-testid="cmd-type-select">
                  <option value="text">Text</option><option value="image">Image</option><option value="audio">Audio</option><option value="video">Video</option>
                </select>
              </div>
              {editing.response_type === "text" ? (
                <div className="space-y-2"><Label>Response Text</Label><Textarea value={editing.response_content} onChange={(e) => setEditing({ ...editing, response_content: e.target.value })} rows={4} placeholder="Bot reply..." data-testid="cmd-content" /></div>
              ) : (
                <div className="space-y-2"><Label>Media URL</Label><Input value={editing.media_url} onChange={(e) => setEditing({ ...editing, media_url: e.target.value })} placeholder="https://..." data-testid="cmd-media-url" /></div>
              )}
              <div className="flex items-center gap-2"><Switch checked={editing.is_active} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} className="data-[state=checked]:bg-[#22c55e]" data-testid="cmd-active-toggle" /><Label>Active</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0f172a] hover:bg-[#1e293b]" data-testid="save-cmd-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
